import React, { useMemo, useState } from 'react';
import { beatCiteLabel } from './ThinGenreMarker';

const emptyBeat = () => ({ name: '', act: 1, purpose: '', cite_kind: 'general', cite_titles: [] });
const emptyAct = () => ({ number: 1, name: '', purpose: '' });
const emptyTrack = () => ({ name: '', purpose: '', entry_position: '', payoff_position: '' });
const emptyTrope = () => ({ name: '', purpose: '', checkable: false });

/** The body Save framework sends, from the editor's fields or a built framework. */
export function frameworkSaveBody({
  frameworkName = '',
  description = '',
  genreTag = '',
  thinGenre = false,
  beats = [],
  acts = [],
  tracks = [],
  tropes = [],
}) {
  return {
    framework_name: frameworkName.trim(),
    description: description.trim(),
    genre_tag: genreTag.trim() || null,
    thin_genre: thinGenre,
    beats: beats.map((beat) => ({
      name: String(beat.name || '').trim(),
      act: parseInt(beat.act, 10),
      purpose: String(beat.purpose || '').trim(),
      cite_kind: beat.cite_kind === 'titles' ? 'titles' : 'general',
      cite_titles: (beat.cite_titles || []).filter(Boolean).slice(0, 2),
    })),
    acts: acts.map((act, index) => ({
      number: parseInt(act.number, 10) || index + 1,
      name: (act.name || `Act ${act.number || index + 1}`).trim(),
      purpose: (act.purpose || '').trim(),
    })),
    tracks: tracks.map((track) => ({
      name: track.name.trim(),
      purpose: track.purpose.trim(),
      entry_position: (track.entry_position || '').trim(),
      payoff_position: (track.payoff_position || '').trim(),
    })),
    tropes: tropes.map((trope) => ({
      name: trope.name.trim(),
      purpose: trope.purpose.trim(),
      checkable: !!trope.checkable,
    })),
  };
}

/** A framework Build with help made, ready to save as it stands. */
export function builtFrameworkSaveBody(built) {
  return frameworkSaveBody({
    frameworkName: built?.framework_name || '',
    description: built?.description || '',
    genreTag: built?.genre_tag || '',
    thinGenre: Boolean(built?.thin_genre),
    beats: built?.beats || [],
    acts: (built?.acts || []).map((a) => ({
      number: a.number || a.act || 1,
      name: a.name || '',
      purpose: a.purpose || '',
    })),
    tracks: (built?.tracks || []).map((t) => ({ ...emptyTrack(), ...t })),
    tropes: (built?.tropes || []).map((t) => ({ ...emptyTrope(), ...t })),
  });
}

export default function FrameworkEditor({
  initial,
  saving,
  error,
  onCancel,
  onSave,
}) {
  const [frameworkName, setFrameworkName] = useState(initial?.framework_name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [genreTag, setGenreTag] = useState(initial?.genre_tag || '');
  const [beats, setBeats] = useState(
    initial?.beats?.length
      ? initial.beats.map((b) => ({
        ...emptyBeat(),
        ...b,
        cite_kind: b.cite_kind || 'general',
        cite_titles: b.cite_titles || [],
      }))
      : [emptyBeat()]
  );
  const thinGenre = Boolean(initial?.thin_genre);
  const [acts, setActs] = useState(
    (initial?.acts || []).map((a) => ({
      number: a.number || a.act || 1,
      name: a.name || '',
      purpose: a.purpose || '',
    }))
  );
  const [tracks, setTracks] = useState(
    (initial?.tracks || []).map((t) => ({
      name: t.name || '',
      purpose: t.purpose || '',
      entry_position: t.entry_position || '',
      payoff_position: t.payoff_position || '',
    }))
  );
  const [tropes, setTropes] = useState(
    (initial?.tropes || []).map((t) => ({
      name: t.name || '',
      purpose: t.purpose || '',
      checkable: !!t.checkable,
    }))
  );

  const canSave = useMemo(() => {
    if (!frameworkName.trim()) return false;
    if (!beats.length) return false;
    if (!beats.every((b) => b.name.trim() && String(b.act).trim() && b.purpose.trim())) {
      return false;
    }
    if (!tropes.every((t) => t.name.trim() && t.purpose.trim())) return false;
    if (!tracks.every((t) => t.name.trim() && t.purpose.trim())) return false;
    return true;
  }, [frameworkName, beats, tropes, tracks]);

  const updateBeat = (index, patch) => {
    setBeats((prev) => prev.map((beat, i) => (i === index ? { ...beat, ...patch } : beat)));
  };

  const moveBeat = (index, delta) => {
    const next = index + delta;
    if (next < 0 || next >= beats.length) return;
    setBeats((prev) => {
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      copy.splice(next, 0, item);
      return copy;
    });
  };

  const handleSave = () => {
    onSave(frameworkSaveBody({
      frameworkName, description, genreTag, thinGenre, beats, acts, tracks, tropes,
    }));
  };

  return (
    <div className="fw-editor">
      <label className="fw-field">
        <span className="fw-field__label">Rename</span>
        <input
          className="fw-input"
          value={frameworkName}
          onChange={(e) => setFrameworkName(e.target.value)}
          maxLength={100}
        />
      </label>
      <label className="fw-field">
        <span className="fw-field__label">Description</span>
        <textarea
          className="fw-textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <label className="fw-field">
        <span className="fw-field__label">Genre tag <span className="fw-optional">(optional)</span></span>
        <input
          className="fw-input"
          value={genreTag}
          onChange={(e) => setGenreTag(e.target.value)}
          maxLength={50}
          placeholder="mystery, romance…"
        />
      </label>

      <div className="fw-beats-head">
        <span className="fw-field__label">Acts</span>
        <button
          type="button"
          className="fw-btn fw-btn--ghost"
          onClick={() => setActs((prev) => [...prev, { ...emptyAct(), number: prev.length + 1 }])}
        >
          Add act
        </button>
      </div>
      <ol className="fw-beat-list">
        {acts.map((act, index) => (
          <li key={index} className="fw-beat-edit">
            <div className="fw-beat-edit__row">
              <label className="fw-field fw-field--act">
                <span className="fw-field__label">Number</span>
                <input
                  className="fw-input"
                  type="number"
                  min={1}
                  value={act.number}
                  onChange={(e) => {
                    const value = e.target.value;
                    setActs((prev) => prev.map((row, i) => (i === index ? { ...row, number: value } : row)));
                  }}
                />
              </label>
              <label className="fw-field fw-field--grow">
                <span className="fw-field__label">Act name</span>
                <input
                  className="fw-input"
                  value={act.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setActs((prev) => prev.map((row, i) => (i === index ? { ...row, name: value } : row)));
                  }}
                />
              </label>
            </div>
            <label className="fw-field">
              <span className="fw-field__label">Purpose <span className="fw-optional">(optional)</span></span>
              <textarea
                className="fw-textarea"
                rows={2}
                value={act.purpose}
                onChange={(e) => {
                  const value = e.target.value;
                  setActs((prev) => prev.map((row, i) => (i === index ? { ...row, purpose: value } : row)));
                }}
              />
            </label>
            <button
              type="button"
              className="fw-link fw-link--danger"
              onClick={() => setActs((prev) => prev.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </li>
        ))}
      </ol>

      <div className="fw-beats-head">
        <span className="fw-field__label">Sequences</span>
        <button
          type="button"
          className="fw-btn fw-btn--ghost"
          onClick={() => setBeats((prev) => [...prev, emptyBeat()])}
        >
          Add sequence
        </button>
      </div>

      <ol className="fw-beat-list">
        {beats.map((beat, index) => (
          <li key={index} className="fw-beat-edit">
            <div className="fw-beat-edit__row">
              <label className="fw-field fw-field--grow">
                <span className="fw-field__label">Sequence name</span>
                <input
                  className="fw-input"
                  value={beat.name}
                  onChange={(e) => updateBeat(index, { name: e.target.value })}
                />
              </label>
              <label className="fw-field fw-field--act">
                <span className="fw-field__label">Act</span>
                <input
                  className="fw-input"
                  type="number"
                  min={1}
                  value={beat.act}
                  onChange={(e) => updateBeat(index, { act: e.target.value })}
                />
              </label>
            </div>
            <label className="fw-field">
              <span className="fw-field__label">Purpose</span>
              <textarea
                className="fw-textarea"
                rows={4}
                value={beat.purpose}
                onChange={(e) => updateBeat(index, { purpose: e.target.value })}
              />
            </label>
            {beatCiteLabel(beat) !== 'Example' ? (
              <p className="fw-cite">{beatCiteLabel(beat)}</p>
            ) : null}
            <div className="fw-beat-edit__tools">
              <button type="button" className="fw-link" onClick={() => moveBeat(index, -1)} disabled={index === 0}>
                Move up
              </button>
              <button
                type="button"
                className="fw-link"
                onClick={() => moveBeat(index, 1)}
                disabled={index === beats.length - 1}
              >
                Move down
              </button>
              <button
                type="button"
                className="fw-link fw-link--danger"
                onClick={() => setBeats((prev) => prev.filter((_, i) => i !== index))}
                disabled={beats.length === 1}
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ol>

      <div className="fw-beats-head">
        <span className="fw-field__label">Tracks <span className="fw-optional">(optional)</span></span>
        <button
          type="button"
          className="fw-btn fw-btn--ghost"
          onClick={() => setTracks((prev) => [...prev, emptyTrack()])}
        >
          Add track
        </button>
      </div>
      <p className="fw-muted">
        Main storylines. Not tropes. Name, purpose, and roughly where it enters and pays off.
      </p>
      <ul className="fw-beat-list">
        {tracks.map((track, index) => (
          <li key={index} className="fw-beat-edit">
            <label className="fw-field">
              <span className="fw-field__label">Track name</span>
              <input
                className="fw-input"
                value={track.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setTracks((prev) => prev.map((row, i) => (i === index ? { ...row, name: value } : row)));
                }}
              />
            </label>
            <label className="fw-field">
              <span className="fw-field__label">Directional purpose</span>
              <textarea
                className="fw-textarea"
                rows={2}
                value={track.purpose}
                onChange={(e) => {
                  const value = e.target.value;
                  setTracks((prev) => prev.map((row, i) => (i === index ? { ...row, purpose: value } : row)));
                }}
              />
            </label>
            <div className="fw-beat-edit__row">
              <label className="fw-field fw-field--grow">
                <span className="fw-field__label">Enters</span>
                <input
                  className="fw-input"
                  value={track.entry_position}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTracks((prev) => prev.map((row, i) => (i === index ? { ...row, entry_position: value } : row)));
                  }}
                />
              </label>
              <label className="fw-field fw-field--grow">
                <span className="fw-field__label">Pays off</span>
                <input
                  className="fw-input"
                  value={track.payoff_position}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTracks((prev) => prev.map((row, i) => (i === index ? { ...row, payoff_position: value } : row)));
                  }}
                />
              </label>
            </div>
            <button
              type="button"
              className="fw-link fw-link--danger"
              onClick={() => setTracks((prev) => prev.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>

      <div className="fw-beats-head">
        <span className="fw-field__label">Tropes <span className="fw-optional">(optional)</span></span>
        <button
          type="button"
          className="fw-btn fw-btn--ghost"
          onClick={() => setTropes((prev) => [...prev, emptyTrope()])}
        >
          Add trope
        </button>
      </div>
      <p className="fw-muted">
        Texture and extras. Check “Look for in conversation” only when coverage check could see it in chat.
      </p>
      <ul className="fw-beat-list">
        {tropes.map((trope, index) => (
          <li key={index} className="fw-beat-edit">
            <label className="fw-field">
              <span className="fw-field__label">Trope name</span>
              <input
                className="fw-input"
                value={trope.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setTropes((prev) => prev.map((row, i) => (i === index ? { ...row, name: value } : row)));
                }}
              />
            </label>
            <label className="fw-field">
              <span className="fw-field__label">Purpose</span>
              <textarea
                className="fw-textarea"
                rows={3}
                value={trope.purpose}
                onChange={(e) => {
                  const value = e.target.value;
                  setTropes((prev) => prev.map((row, i) => (i === index ? { ...row, purpose: value } : row)));
                }}
              />
            </label>
            <label className="fw-check">
              <input
                type="checkbox"
                checked={!!trope.checkable}
                onChange={(e) => {
                  const value = e.target.checked;
                  setTropes((prev) => prev.map((row, i) => (i === index ? { ...row, checkable: value } : row)));
                }}
              />
              Look for in conversation
            </label>
            <button
              type="button"
              className="fw-link fw-link--danger"
              onClick={() => setTropes((prev) => prev.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>

      {error && <p className="fw-error" role="alert">{error}</p>}

      <div className="fw-dialog-actions">
        <button type="button" className="fw-btn fw-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="fw-btn fw-btn--primary"
          onClick={handleSave}
          disabled={!canSave || saving}
        >
          {saving ? 'Saving…' : 'Save as my Framework'}
        </button>
      </div>
    </div>
  );
}
